import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { sendChatbotMessage, IS_API_CONFIGURED } from '@/lib/chatbotApi';
import type { ChatbotRequest } from '@/lib/chatbotApi';
import { useAuth } from '@/hooks/useAuth';

const SESSION_KEY = 'gbcospack_chatbot_session_id';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  safetyStatus?: string;
  quoteCta?: { enabled: boolean; label: string; url: string } | null;
  suggestedQuestions?: string[];
  isError?: boolean;
  isConfigError?: boolean;
  debugInfo?: string;
}

function generateId() {
  return Math.random().toString(36).substring(2, 9);
}

function getPageContext(pathname: string, search: string): ChatbotRequest['page_context'] {
  const ctx: ChatbotRequest['page_context'] = {
    page_type: 'other',
    product_id: null,
    category: null,
    sku: null,
  };

  if (pathname === '/' || pathname === '') {
    ctx.page_type = 'home';
  } else if (pathname.startsWith('/my/quotes')) {
    ctx.page_type = 'quote';
  } else if (pathname.startsWith('/my/finish-previews')) {
    ctx.page_type = 'customizer';
  } else if (pathname.startsWith('/product/')) {
    ctx.page_type = 'product_detail';
    const parts = pathname.split('/');
    if (parts[2]) ctx.product_id = parts[2];
  } else if (pathname.startsWith('/packaging') || pathname.startsWith('/products')) {
    const params = new URLSearchParams(search);
    const category = params.get('category');
    if (category) ctx.category = category;
  }

  return ctx;
}

function mapLocale(i18nLang: string): string {
  if (i18nLang.startsWith('ko')) return 'ko';
  if (i18nLang.startsWith('zh')) return 'zh';
  return 'en';
}

function buildErrorText(response: { safety_status: string; answer?: string; _debug?: { has_dashscope_key?: boolean; has_deepseek_key?: boolean; dashscope_error?: string; deepseek_error?: string; rag_error?: string; matched_chunks?: number; [key: string]: unknown } }, locale: string): string {
  if (response.safety_status !== 'error') return response.answer || '';

  const debug = response._debug;
  const reasons: string[] = [];
  if (debug?.has_dashscope_key === false) reasons.push(locale === 'ko' ? 'DashScope API 키가 설정되지 않았습니다' : locale === 'zh' ? 'DashScope API 密钥未设置' : 'DashScope API key is missing');
  if (debug?.has_deepseek_key === false) reasons.push(locale === 'ko' ? 'DeepSeek API 키가 설정되지 않았습니다' : locale === 'zh' ? 'DeepSeek API 密钥未设置' : 'DeepSeek API key is missing');
  if (debug?.dashscope_error) reasons.push(`${locale === 'ko' ? 'DashScope 오류' : locale === 'zh' ? 'DashScope 错误' : 'DashScope error'}: ${debug.dashscope_error}`);
  if (debug?.deepseek_error) reasons.push(`${locale === 'ko' ? 'DeepSeek 오류' : locale === 'zh' ? 'DeepSeek 错误' : 'DeepSeek error'}: ${debug.deepseek_error}`);
  if (debug?.rag_error) reasons.push(`${locale === 'ko' ? '검색 오류' : locale === 'zh' ? '搜索错误' : 'Search error'}: ${debug.rag_error}`);
  if (debug?.matched_chunks === 0) reasons.push(locale === 'ko' ? '관련 문서를 찾을 수 없습니다' : locale === 'zh' ? '未找到相关文档' : 'No relevant documents found');

  let base = locale === 'ko'
    ? '죄송합니다. 지금 답변을 생성할 수 없습니다.'
    : locale === 'zh'
      ? '抱歉，目前无法生成回答。'
      : 'Sorry, I could not generate an answer right now.';

  if (reasons.length > 0) {
    base += locale === 'ko'
      ? `\n\n📋 원인: ${reasons.join(' / ')}`
      : locale === 'zh'
        ? `\n\n📋 原因: ${reasons.join(' / ')}`
        : `\n\n📋 Reason: ${reasons.join(' / ')}`;
  }

  base += locale === 'ko'
    ? '\n\n⏳ 관리자에게 API 키 설정을 확인해 달라고 요청해 주세요.'
    : locale === 'zh'
      ? '\n\n⏳ 请联系管理员确认 API 密钥设置。'
      : '\n\n⏳ Please ask the admin to check API key settings.';

  return base;
}

export default function FloatingChatbot() {
  const { t, i18n } = useTranslation('chatbot');
  const location = useLocation();
  const auth = useAuth();

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [hasStarted, setHasStarted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const locale = useMemo(() => mapLocale(i18n.language || 'en'), [i18n.language]);
  const pageContext = useMemo(
    () => getPageContext(location.pathname, location.search),
    [location.pathname, location.search]
  );
  const country = useMemo(() => auth.company?.country || null, [auth.company]);
  const currency = useMemo(() => auth.profile?.default_currency || null, [auth.profile]);

  // Load session from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(SESSION_KEY);
    if (saved) {
      setSessionId(saved);
    }
  }, []);

  // Initialize welcome message when opened and no messages
  useEffect(() => {
    if (open && messages.length === 0 && !hasStarted) {
      setMessages([
        {
          id: generateId(),
          role: 'assistant',
          text: t('chatbot.welcomeMessage', 'Hello. I can help with cosmetic packaging selection, quotation requests, AI finishing previews, and order steps.'),
          safetyStatus: 'ok',
        },
      ]);
    }
  }, [open, messages.length, hasStarted, t]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open, loading]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  // Close panel on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open]);

  const handleNewChat = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    setSessionId(null);
    setMessages([]);
    setHasStarted(false);
    setInput('');
    setLoading(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSend = useCallback(
    async (text: string) => {
      if (!text.trim() || loading) return;
      const userText = text.trim();

      setMessages((prev) => [
        ...prev,
        { id: generateId(), role: 'user', text: userText },
      ]);
      setInput('');
      setLoading(true);
      setHasStarted(true);

      // If API is not configured, show a clear message instead of failing
      if (!IS_API_CONFIGURED) {
        setMessages((prev) => [
          ...prev,
          {
            id: generateId(),
            role: 'assistant',
            text: t('chatbot.notConfigured', 'The chatbot API is not yet connected. Please ask the admin to set the backend URL.'),
            isError: true,
            isConfigError: true,
            safetyStatus: 'error',
          },
        ]);
        setLoading(false);
        return;
      }

      try {
        const response = await sendChatbotMessage({
          session_id: sessionId,
          message: userText,
          locale,
          country,
          currency,
          page_context: pageContext,
        });

        if (response.session_id) {
          localStorage.setItem(SESSION_KEY, response.session_id);
          setSessionId(response.session_id);
        }

        const isError = response.safety_status === 'error';
        const assistantMsg: ChatMessage = {
          id: generateId(),
          role: 'assistant',
          text: isError
            ? buildErrorText(response, locale)
            : (response.answer || ''),
          safetyStatus: response.safety_status,
          quoteCta: response.quote_request_cta,
          suggestedQuestions: response.suggested_questions,
          isError,
        };

        setMessages((prev) => [...prev, assistantMsg]);
      } catch (err) {
        console.error('[Chatbot] API error:', err);
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        setMessages((prev) => [
          ...prev,
          {
            id: generateId(),
            role: 'assistant',
            text: locale === 'ko'
              ? `죄송합니다. 서버와 연결할 수 없습니다.\n\n📋 오류: ${errorMsg}`
              : locale === 'zh'
                ? `抱歉，无法连接服务器。\n\n📋 错误: ${errorMsg}`
                : `Sorry, I could not connect to the server.\n\n📋 Error: ${errorMsg}`,
            isError: true,
            safetyStatus: 'error',
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [loading, locale, country, currency, pageContext, sessionId, t]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend(input);
      }
    },
    [input, handleSend]
  );

  const handleStarterClick = (question: string) => {
    handleSend(question);
  };

  const showStarterQuestions = !hasStarted && messages.length === 1 && messages[0]?.role === 'assistant' && !loading;

  return (
    <>
      {/* Chat Panel */}
      {open && (
        <div
          ref={containerRef}
          className="fixed z-[100] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100
            inset-x-4 bottom-24 h-[70vh] max-h-[600px]
            md:w-[380px] md:h-[580px] md:max-h-[600px] md:right-5 md:left-auto md:bottom-24"
          role="dialog"
          aria-label={t('chatbot.headerTitle', 'GB CosPack Assistant')}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-teal-600 flex-shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <i className="ri-robot-2-line text-white text-base" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">
                  {t('chatbot.headerTitle', 'GB CosPack Assistant')}
                </p>
                <p className="text-[11px] text-teal-100 truncate">
                  {t('chatbot.headerSubtitle', 'Ask about packaging, quotes, and orders.')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <button
                onClick={handleNewChat}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                title={t('chatbot.newChat', 'New chat')}
                aria-label={t('chatbot.newChat', 'New chat')}
              >
                <i className="ri-refresh-line text-lg" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                aria-label={t('chatbot.close', 'Close')}
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
          </div>

          {/* Config warning banner */}
          {!IS_API_CONFIGURED && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-100 flex-shrink-0">
              <i className="ri-error-warning-line text-amber-500 text-sm flex-shrink-0" />
              <p className="text-[11px] text-amber-700 leading-snug">
                {t('chatbot.notConfigured', 'The chatbot API is not yet connected. Please ask the admin to set the backend URL.')}
              </p>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-teal-600 text-white rounded-br-md'
                      : msg.isError
                        ? 'bg-red-50 text-red-700 border border-red-100 rounded-bl-md'
                        : msg.isConfigError
                          ? 'bg-amber-50 text-amber-700 border border-amber-100 rounded-bl-md'
                          : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
                  }`}
                >
                  <div className="whitespace-pre-wrap">{msg.text}</div>

                  {/* Out of scope note */}
                  {msg.safetyStatus === 'out_of_scope' && (
                    <p className="mt-1.5 text-[11px] text-gray-500 border-t border-gray-200/80 pt-1.5">
                      {t('chatbot.outOfScopeNote', 'Please ask a packaging, quote, or order-related question.')}
                    </p>
                  )}

                  {/* Quote CTA — removed per request */}

                  {/* Suggested questions — only show on successful answer */}
                  {!msg.isError && !msg.isConfigError && msg.suggestedQuestions && msg.suggestedQuestions.length > 0 && (
                    <div className="mt-2 space-y-1.5 border-t border-gray-200/80 pt-2">
                      <p className="text-[11px] text-gray-500">
                        {t('chatbot.suggestedQuestions', 'Suggested questions')}
                      </p>
                      {msg.suggestedQuestions.map((q, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSend(q)}
                          className="block w-full text-left px-2.5 py-1.5 text-xs text-teal-700 bg-teal-50 rounded-lg border border-teal-200 hover:bg-teal-100 transition-colors cursor-pointer"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <span className="text-xs text-gray-500">
                      {t('chatbot.loading', 'Generating a response...')}
                    </span>
                    <div className="flex items-center gap-1">
                      <div
                        className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-bounce"
                        style={{ animationDelay: '0ms' }}
                      />
                      <div
                        className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-bounce"
                        style={{ animationDelay: '150ms' }}
                      />
                      <div
                        className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-bounce"
                        style={{ animationDelay: '300ms' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Starter questions */}
            {showStarterQuestions && (
              <div className="space-y-1.5 pt-1">
                {[0, 1, 2, 3].map((idx) => {
                  const question = t(`chatbot.starter.${idx}`, '');
                  if (!question) return null;
                  return (
                    <button
                      key={idx}
                      onClick={() => handleStarterClick(question)}
                      className="block w-full text-left px-3 py-2 text-sm text-teal-700 bg-white rounded-lg border border-teal-200 hover:bg-teal-50 hover:border-teal-300 transition-colors cursor-pointer shadow-sm"
                    >
                      <span className="flex items-center gap-2">
                        <i className="ri-question-line text-teal-500 flex-shrink-0" />
                        <span className="leading-snug">{question}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="flex items-center gap-2 px-3 py-3 border-t border-gray-100 bg-white flex-shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('chatbot.inputPlaceholder', 'Type your message...')}
              disabled={loading}
              className="flex-1 px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={t('chatbot.inputPlaceholder', 'Type your message...')}
            />
            <button
              onClick={() => handleSend(input)}
              disabled={!input.trim() || loading}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer flex-shrink-0"
              aria-label={t('chatbot.send', 'Send')}
            >
              <i className="ri-send-plane-fill text-base" />
            </button>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setOpen(!open)}
        className={`fixed z-[100] rounded-full shadow-2xl flex items-center justify-center text-white transition-all duration-200 cursor-pointer hover:scale-110 active:scale-95
          ${open ? 'bg-gray-600' : 'bg-teal-600'}
          w-[45px] h-[45px] right-4 bottom-4
          md:w-[51px] md:h-[51px] md:right-5 md:bottom-5`}
        aria-label={open ? t('chatbot.close', 'Close') : t('chatbot.buttonLabel', 'Chat')}
      >
        {open ? (
          <i className="ri-close-line text-lg" />
        ) : (
          <i className="ri-message-3-line text-lg" />
        )}
      </button>

      {/* Button Label Tooltip */}
      {!open && (
        <span className="fixed z-[100] right-[calc(1rem+45px+0.25rem)] bottom-[calc(1rem+0.625rem)] md:right-[calc(1.25rem+51px+0.25rem)] md:bottom-[calc(1.25rem+0.75rem)] bg-white text-teal-700 text-[11px] font-medium px-2.5 py-1 rounded-full shadow-md border border-teal-100 pointer-events-none whitespace-nowrap">
          {t('chatbot.buttonLabel', 'Chat')}
        </span>
      )}
    </>
  );
}